<template>
    <ul class="campaign-list">
        <li v-for="campaign in rows" :key="campaign.key" class="campaign-list__item">
            <span :class="['campaign-type', `campaign-type--${campaign.type}`]">{{ CAMPAIGN_TYPE_LABEL[campaign.type] }}</span>
            <a class="campaign-list__name" :href="campaign.url" :title="campaign.name">
                <HighlightText :text="campaign.name" :query="query" />
            </a>
            <span class="campaign-list__id">#{{ campaign.id }}</span>
            <MarkupChip
                v-if="campaign.markup && partner"
                class="campaign-list__markup"
                :markup="campaign.markup"
                :partner-markup="partner.markup"
                :editable="canEditMarkup"
                @edit="emit('edit-markup', campaign)"
            />
            <span :class="['campaign-status', `campaign-status--${campaign.display.displayStatus}`]">
                {{ campaign.display.displayLabel }}
            </span>
        </li>
    </ul>
</template>

<script setup>
import { computed } from 'vue';
import HighlightText from './HighlightText.vue';
import MarkupChip from './MarkupChip.vue';
import { CAMPAIGN_TYPE_LABEL } from '../../../constants/structure';
import { getCampaignDisplayStatus } from '../../../utils/campaignStatus';
import { canEditAnyMarkupLevel } from '../../../utils/markupUtils';

const props = defineProps({
    campaigns: { type: Array, required: true },
    /** Партнёр ветки: от него RTB-кампании наследуют наценки. */
    partner: { type: Object, default: null },
    query: { type: String, default: '' },
});

const emit = defineEmits(['edit-markup']);

const canEditMarkup = computed(() => canEditAnyMarkupLevel(props.partner));

const rows = computed(() => props.campaigns.map(campaign => ({
    ...campaign,
    display: getCampaignDisplayStatus(campaign.status),
})));
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;

.campaign-list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.campaign-list__item {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    min-width: 0;
    padding: $spacing-xs 0;
    font-size: $font-size-sm;
}

.campaign-list__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $admin-text-primary;
    text-decoration: none;

    &:hover { color: $admin-primary-color; text-decoration: underline; }
}

.campaign-list__id {
    flex-shrink: 0;
    font-size: $font-size-xs;
    color: $admin-text-light;
}

.campaign-list__markup {
    margin-left: auto;
}

.campaign-type,
.campaign-status {
    flex-shrink: 0;
    padding: 0.0625rem $spacing-sm;
    border-radius: $radius-sm;
    font-size: 0.6875rem;
    font-weight: 600;
    white-space: nowrap;
}

.campaign-type {
    &--guaranteed { background: #ecfdf5; color: #047857; }
    &--rtb { background: #eff6ff; color: #1d4ed8; }
    &--rtb_package { background: #f5f3ff; color: #6d28d9; }
    &--rtb_poi { background: #ecfeff; color: #0e7490; }
}

.campaign-status {
    margin-left: auto;
    font-weight: 500;
    background: $admin-bg-hover;
    color: $admin-text-secondary;

    &--active { background: $admin-success-bg; color: $admin-success-color; }
    &--paused { background: $admin-warning-bg; color: $admin-warning-color; }
    &--moderation { background: #eff6ff; color: #1e40af; }
}

.campaign-list__markup + .campaign-status {
    margin-left: 0;
}
</style>
